/**
 * Patient Context Bar Component
 *
 * Main component that displays patient information and context indicators
 * with hover popovers for medications, diagnoses, allergies, and last encounter.
 */

import React, { useCallback, useState } from 'react';
import { Box, Collapse } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { Patient } from '@ts/types';

import { useIndicatorHover } from '../hooks/useIndicatorHover';
import { usePatientContext } from '../hooks/usePatientContext';

import {
  PatientAllergiesPopover,
  PatientDiagnosesEditModal,
  PatientDiagnosesPopover,
  PatientLastEncounterPopover,
  PatientMedicationsEditModal,
  PatientMedicationsPopover,
} from './index';
import { PatientDisplay } from './PatientDisplay';
import { PatientIndicators } from './PatientIndicators';

export interface PatientContextBarProps {
  /** Patient to display context for */
  patient: Patient | null;
  /** Callback when patient is changed (unselected) */
  onPatientChange?: (patient: Patient | undefined) => void;
  /** Callback when viewing last encounter note */
  onViewLastEncounterNote?: (noteId: string) => void;
  /** Callback when patient name is clicked (e.g., to navigate to patient profile) */
  onPatientClick?: (patientId: string) => void;
  /** Whether patient selection can be edited */
  editable?: boolean;
}

/**
 * Patient Context Bar
 *
 * Displays patient information with context indicators that show
 * detailed information on hover (medications, diagnoses, allergies, last encounter).
 */
export const PatientContextBar: React.FC<PatientContextBarProps> = ({
  patient,
  onPatientChange,
  onViewLastEncounterNote,
  onPatientClick,
  editable = true,
}) => {
  // Fetch patient context data
  const {
    medications,
    diagnoses,
    allergies,
    lastEncounter,
    loadingState,
    medicationsModuleInfo,
    diagnosesModuleInfo,
  } = usePatientContext(patient?.patient_id || null);

  // Event tracking
  const { patient: patientTracking } = useEventTracking();

  // Modal state
  const [medicationsModalOpen, setMedicationsModalOpen] = useState(false);
  const [diagnosesModalOpen, setDiagnosesModalOpen] = useState(false);

  // Hover state for each indicator
  const medicationsHover = useIndicatorHover();
  const diagnosesHover = useIndicatorHover();
  const allergiesHover = useIndicatorHover();
  const lastEncounterHover = useIndicatorHover();

  const handlePatientClose = () => {
    if (onPatientChange) {
      onPatientChange(undefined);
    }
  };

  const handleMedicationsClick = useCallback(() => {
    if (!patient?.patient_id) return;
    setMedicationsModalOpen(true);
    patientTracking.updated(patient.patient_id, {
      source: 'context_bar',
      fields_updated: ['medications_edit_modal_opened'],
    });
  }, [patient?.patient_id, patientTracking]);

  const handleDiagnosesClick = useCallback(() => {
    if (!patient?.patient_id) return;
    setDiagnosesModalOpen(true);
    patientTracking.updated(patient.patient_id, {
      source: 'context_bar',
      fields_updated: ['diagnoses_edit_modal_opened'],
    });
  }, [patient?.patient_id, patientTracking]);

  const handleMedicationsUpdated = useCallback(() => {
    if (!patient?.patient_id) return;
    patientTracking.updated(patient.patient_id, {
      source: 'context_bar',
      fields_updated: ['medications'],
    });
  }, [patient?.patient_id, patientTracking]);

  const handleDiagnosesUpdated = useCallback(() => {
    if (!patient?.patient_id) return;
    patientTracking.updated(patient.patient_id, {
      source: 'context_bar',
      fields_updated: ['diagnoses'],
    });
  }, [patient?.patient_id, patientTracking]);

  return (
    <>
      <Collapse in={!!patient}>
        <Box
          sx={{
            backgroundColor: '#FCFCFC',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              maxWidth: 1400,
              mx: 'auto',
            }}
          >
            {patient && (
              <>
                <PatientDisplay
                  patient={patient}
                  onClose={editable ? handlePatientClose : undefined}
                  onPatientClick={onPatientClick}
                />
                <PatientIndicators
                  medicationsHover={medicationsHover}
                  diagnosesHover={diagnosesHover}
                  allergiesHover={allergiesHover}
                  lastEncounterHover={lastEncounterHover}
                  onMedicationsClick={handleMedicationsClick}
                  onDiagnosesClick={handleDiagnosesClick}
                />
              </>
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Medications Popover */}
      <PatientMedicationsPopover
        medications={medications}
        anchorEl={medicationsHover.anchorEl}
        open={medicationsHover.isHovered && !!medicationsHover.anchorEl && !!patient}
        onClose={() => {
          medicationsHover.handlePopoverLeave();
        }}
        onMouseEnter={medicationsHover.handlePopoverEnter}
        onMouseLeave={medicationsHover.handlePopoverLeave}
        isLoading={loadingState.medications}
        onEdit={handleMedicationsClick}
      />

      {/* Diagnoses Popover */}
      <PatientDiagnosesPopover
        diagnoses={diagnoses}
        anchorEl={diagnosesHover.anchorEl}
        open={diagnosesHover.isHovered && !!diagnosesHover.anchorEl && !!patient}
        onClose={() => {
          diagnosesHover.handlePopoverLeave();
        }}
        onMouseEnter={diagnosesHover.handlePopoverEnter}
        onMouseLeave={diagnosesHover.handlePopoverLeave}
        isLoading={loadingState.diagnoses}
        onEdit={handleDiagnosesClick}
      />

      {/* Allergies Popover */}
      <PatientAllergiesPopover
        allergies={allergies}
        anchorEl={allergiesHover.anchorEl}
        open={allergiesHover.isHovered && !!allergiesHover.anchorEl && !!patient}
        onClose={() => {
          allergiesHover.handlePopoverLeave();
        }}
        onMouseEnter={allergiesHover.handlePopoverEnter}
        onMouseLeave={allergiesHover.handlePopoverLeave}
        isLoading={loadingState.allergies}
      />

      {/* Last Encounter Popover */}
      <PatientLastEncounterPopover
        data={lastEncounter}
        anchorEl={lastEncounterHover.anchorEl}
        open={lastEncounterHover.isHovered && !!lastEncounterHover.anchorEl && !!patient}
        onClose={() => {
          lastEncounterHover.handlePopoverLeave();
        }}
        onMouseEnter={lastEncounterHover.handlePopoverEnter}
        onMouseLeave={lastEncounterHover.handlePopoverLeave}
        isLoading={loadingState.lastEncounter}
        onViewNote={onViewLastEncounterNote}
      />

      {/* Medications Edit Modal */}
      {patient?.patient_id && medicationsModuleInfo && (
        <PatientMedicationsEditModal
          open={medicationsModalOpen}
          onClose={() => setMedicationsModalOpen(false)}
          patientId={patient.patient_id}
          medications={medications}
          moduleInfo={medicationsModuleInfo}
          onMedicationsUpdated={handleMedicationsUpdated}
        />
      )}

      {/* Diagnoses Edit Modal */}
      {patient?.patient_id && diagnosesModuleInfo && (
        <PatientDiagnosesEditModal
          open={diagnosesModalOpen}
          onClose={() => setDiagnosesModalOpen(false)}
          patientId={patient.patient_id}
          diagnoses={diagnoses}
          moduleInfo={diagnosesModuleInfo}
          onDiagnosesUpdated={handleDiagnosesUpdated}
        />
      )}
    </>
  );
};
